import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PanelLeft24Regular, PanelRight24Regular } from '@fluentui/react-icons';
import { 
  MdCall, 
  MdWavingHand, 
  MdBeachAccess, 
  MdAccessTime,
  MdWorkspaces,
  MdPoll,
  MdDialpad,
  MdPhone,
  MdCallEnd,
  MdVolumeUp,
  MdCheckCircle,
  MdCalendarToday,
  MdSchedule,
  MdTextFields,
  MdPerson,
  MdPersonOff,
  MdPhoneDisabled,
  MdPhoneForwarded
} from 'react-icons/md';
import { HiUsers } from 'react-icons/hi2';
import './CSS/NodesPalette.css';
/**
 * Composant NodesPalette - Liste des nœuds disponibles par catégorie
 * Partie 1 : Générale (Resource Account,Greeting, Holidays, Open Hours, Workflow, Survey)
 * Partie 2 : Routing (IVR, Transfer, Disconnect, Prompt, Check Variable, Check Days, Time, Regex)
 * Partie 3 : Target/Events (, Agent Connected, Customer Connected, Call Terminated, etc.)
 */
const NodesPalette = ({ callFlowData }) => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('generale');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  
  // Debug: Vérifier l'état
  
  // Vérifier si un node greeting existe déjà (au moins un message actif)
  const hasGreetingNode = React.useMemo(() => {
    if (!callFlowData?.greeting) return false;
    
    const greetingMessages = callFlowData.greeting;
    const enabledMessages = Object.entries(greetingMessages).filter(
      ([key, msg]) => msg?.enabled === true
    );
    
    return enabledMessages.length > 0;
  }, [callFlowData]);

  // Vérifier si un node holidays existe déjà (weekendDays ou daysOff configurés)
  const hasHolidaysNode = React.useMemo(() => {
    const holidaysData = callFlowData?.deskOpen?.holidays || {};
    const hasWeekendDays = holidaysData.weekendDays && holidaysData.weekendDays.length > 0;
    const hasHolidayDays = holidaysData.daysOff && holidaysData.daysOff.length > 0;
    return hasWeekendDays || hasHolidayDays;
  }, [callFlowData]);

  // Vérifier si un node open-hours existe déjà (au moins un jour avec des heures configurées)
  const hasOpenHoursNode = React.useMemo(() => {
    const openHoursData = callFlowData?.deskOpen?.openHours || {};
    return Object.keys(openHoursData).some(key => {
      const dayRanges = openHoursData[key];
      return Array.isArray(dayRanges) && dayRanges.length > 0;
    });
  }, [callFlowData]);
  
  // Map des icônes par node ID
  const nodeIcons = {
    'resource-account': MdCall,
    'greeting': MdWavingHand,
    'holidays': MdBeachAccess,
    'open-hours': MdAccessTime,
    'survey': MdPoll,
    'ivr': MdDialpad,
    'transfer-user': MdPerson,
    'transfer-queue': HiUsers,
    'transfer-phone': MdPhone,
    'disconnect': MdCallEnd,
    'prompt': MdVolumeUp,
    'check-variable': MdCheckCircle,
    'check-days': MdCalendarToday,
    'check-time': MdSchedule,
    'check-regex': MdTextFields,
    'agent-connected': MdPerson,
    'customer-abandoned': MdPersonOff,
    'call-connected': MdPhoneForwarded,
    'call-ended': MdPhoneDisabled,
    'agent-declined': MdPersonOff,
  };
  
  /** Catégorie 1 : Générale */
  const generaleNodes = [
    
    {
      id: 'greeting',
      title: t('GREETING_MESSAGE'),
      description: t('GREETING_DESCRIPTION'),
    },
    {
      id: 'holidays',
      title: t('HOLIDAYS'),
      description: t('HOLIDAYS_DESCRIPTION'),
    },
    {
      id: 'open-hours',
      title: t('OPEN_HOURS'),
      description: t('OPEN_HOURS_DESCRIPTION'),
    },
    {
      id: 'survey',
      title: t('SURVEY'),
      description: t('SURVEY_DESCRIPTION'),
    },
  ];
  /** Catégorie 2 : Routing */
  const routingNodes = [
    {
      id: 'ivr',
      title: t('IVR'),
      description: t('IVR_DESCRIPTION'),
    },
    {
      id: 'transfer-user',
      title: t('TRANSFER_TO_USER'),
      description: t('TRANSFER_TO_USER_DESCRIPTION'),
    },
    {
      id: 'transfer-queue',
      title: t('TRANSFER_TO_QUEUE'),
      description: t('TRANSFER_TO_QUEUE_DESCRIPTION'),
    },
    {
      id: 'transfer-phone',
      title: t('TRANSFER_TO_PHONE'),
      description: t('TRANSFER_TO_PHONE_DESCRIPTION'),
    },
    {
      id: 'disconnect',
      title: t('DISCONNECT'),
      description: t('DISCONNECT_DESCRIPTION'),
    },
    {
      id: 'prompt',
      title: t('PROMPT'),
      description: t('PROMPT_DESCRIPTION'),
    },
    {
      id: 'check-variable',
      title: t('CHECK_VARIABLE'),
      description: t('CHECK_VARIABLE_DESCRIPTION'),
    },
    {
      id: 'check-days',
      title: t('CHECK_DAYS'),
      description: t('CHECK_DAYS_DESCRIPTION'),
    },
    {
      id: 'check-time',
      title: t('CHECK_TIME'),
      description: t('CHECK_TIME_DESCRIPTION'),
    },
    {
      id: 'check-regex',
      title: t('CHECK_REGEX'),
      description: t('CHECK_REGEX_DESCRIPTION'),
    },
  ];
  /** Catégorie 3 : Target/Events (depuis WorkFlowComponent.js) */
  const targetNodes = [
    {
      id: 'agent-connected',
      title: t('AGENT_CONNECTED'),
      description: t('AGENT_CONNECTED_DESCRIPTION'),
    },
    {
      id: 'customer-abandoned',
      title: t('CUSTOMER_ABANDONED'),
      description: t('CUSTOMER_ABANDONED_DESCRIPTION'),
    },
    {
      id: 'call-connected',
      title: t('CALL_CONNECTED'),
      description: t('CALL_CONNECTED_DESCRIPTION'),
    },
    {
      id: 'call-ended',
      title: t('CALL_ENDED'),
      description: t('CALL_ENDED_DESCRIPTION'),
    },
    {
      id: 'agent-declined',
      title: t('AGENT_DECLINED'),
      description: t('AGENT_DECLINED_DESCRIPTION'),
    },
  ];
  const categories = [
    { id: 'generale', label: t('GENERAL'), nodes: generaleNodes },
    { id: 'routing', label: t('ROUTING'), nodes: routingNodes },
    { id: 'target', label: t('TRIGGER'), nodes: targetNodes },
  ];
  const currentNodes = categories.find((cat) => cat.id === activeCategory)?.nodes || [];
  
  
  return (
    <div className={`nodes-palette-wrapper ${isCollapsed ? 'collapsed' : ''}`}>
      <aside className="nodes-palette-container">
        {/* Bouton toggle en haut */}
        <div className="palette-header-toggle">
          <button
            className="palette-toggle-top-btn"
            onClick={() => {
              setIsCollapsed(!isCollapsed);
            }}
            title={isCollapsed ? t('EXPAND_PALETTE') : t('COLLAPSE_PALETTE')}
          >
            {isCollapsed ? <PanelRight24Regular /> : <PanelLeft24Regular />}
          </button>
        </div>
        {/* Onglets de catégories */}
        <div className="palette-categories">
          {categories.map((category, index) => (
            <button
              key={category.id}
              className={`category-tab ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => {
                if (isCollapsed && index === 0) {
                  // En mode collapsed, le premier onglet ouvre le menu popup
                  setShowCategoryMenu(!showCategoryMenu);
                } else {
                  // En mode normal, changer directement de catégorie
                  setActiveCategory(category.id);
                }
              }}
              data-initial={category.label.charAt(0)}
            >
              {category.label}
            </button>
          ))}
          
          {/* Popup menu en mode collapsed */}
          {isCollapsed && showCategoryMenu && (
            <div className="category-popup-menu">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`category-popup-item ${activeCategory === category.id ? 'active' : ''}`}
                  onClick={() => {
                    setActiveCategory(category.id);
                    setShowCategoryMenu(false);
                  }}
                >
                  {category.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Liste des nœuds */}
        <div className="palette-nodes-list">
          {currentNodes.map((node) => {
            const IconComponent = nodeIcons[node.id];
            // Vérifier si le node doit être désactivé
            const isDisabled = 
              (node.id === 'greeting' && hasGreetingNode) ||
              (node.id === 'holidays' && hasHolidaysNode) ||
              (node.id === 'open-hours' && hasOpenHoursNode);
            
            // Message de tooltip selon le node
            let disabledMessage = '';
            if (isDisabled) {
              if (node.id === 'greeting') {
                disabledMessage = t('NODE_GREETING_EXISTS');
              } else if (node.id === 'holidays') {
                disabledMessage = t('NODE_HOLIDAYS_EXISTS');
              } else if (node.id === 'open-hours') {
                disabledMessage = t('NODE_OPEN_HOURS_EXISTS');
              }
            }
            
            return (
              <div
                key={node.id}
                className={`palette-node-item ${isDisabled ? 'disabled' : ''}`}
                draggable={!isDisabled}
                onDragStart={(e) => {
                  if (isDisabled) {
                    e.preventDefault();
                    return;
                  }
                  e.dataTransfer.setData('application/reactflow', JSON.stringify(node));
                  e.dataTransfer.effectAllowed = 'move';
                }}
                title={isDisabled ? disabledMessage : node.description}
              >
                <div className="node-item-content">
                  {IconComponent && <IconComponent className="node-item-icon" size={20} />}
                  <div className="node-item-description">{node.title}</div>
                </div>
              </div>
            );
          })}
        </div>
      </aside>
    </div>
  );
};
export default NodesPalette;
